import React, { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { SlidersHorizontal, Zap, Video, Tag, Palette, Clapperboard, Image as ImageIcon, GalleryHorizontal, CircleDashed, Film, Lock, CheckSquare, File as FileIcon } from 'lucide-react';
import { ChannelIconsRow } from './ChannelIcons';
import { getStateMeta, getPostChannels, getChannelMeta, getPillarMeta } from '../lib/contentStates';
import { contentCalendarAPI } from '../lib/api';

// Campos opcionales de la casilla del calendario. Portada, título, estado, tipo, canales, responsable y color portada siempre se ven.
export const CALENDAR_OPTIONAL_FIELDS = [
  { id: 'pilar', label: 'Pilar de contenido', Icon: Tag },
  { id: 'automatizacion', label: 'Automatización', Icon: Zap },
  { id: 'quien_graba', label: 'Quién graba', Icon: Video },
];
const STORAGE_KEY = 'karma-calendar-visible-fields-v2';
const DEFAULTS = { pilar: true, automatizacion: false, quien_graba: true };

export function useCalendarVisibleFields() {
  const [visible, setVisible] = useState(() => {
    try { return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')) }; } catch { return DEFAULTS; }
  });
  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(visible)); } catch { /* noop */ } }, [visible]);
  return [visible, (id, v) => setVisible((s) => ({ ...s, [id]: v }))];
}

export function CalendarFieldsSelector({ visible, onToggle }) {
  const active = CALENDAR_OPTIONAL_FIELDS.filter((f) => visible[f.id]).length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="calendar-fields-btn">
          <SlidersHorizontal className="w-4 h-4 mr-1.5" /> Campos visibles
          <span className="ml-1.5 text-[10px] rounded-full bg-muted px-1.5 py-px">{active}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3" data-testid="calendar-fields-popover">
        <p className="text-xs font-semibold mb-1">En cada casilla, mostrar también…</p>
        <p className="text-[11px] text-muted-foreground mb-3">Estado, tipo, redes, título, responsable, portada y color de portada se ven siempre. Al pasar el ratón ves la ficha completa.</p>
        <div className="space-y-2">
          {CALENDAR_OPTIONAL_FIELDS.map((f) => (
            <label key={f.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`calendar-field-toggle-${f.id}`}>
              <Checkbox checked={!!visible[f.id]} onCheckedChange={(v) => onToggle(f.id, !!v)} />
              {f.Icon && <f.Icon className="w-3.5 h-3.5 text-muted-foreground" />}
              {f.label}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Mini-chips opcionales bajo el título de la casilla
export function CellOptionalChips({ post, visible, fg }) {
  const chips = [];
  if (visible.automatizacion && post.automatizacion) chips.push({ key: 'auto', Icon: Zap, text: 'Auto' });
  if (visible.quien_graba && post.quien_graba) chips.push({ key: 'quien', Icon: Video, text: post.quien_graba });
  const pil = getPillarMeta(post.pilar);
  if (visible.pilar && pil) chips.push({ key: 'pilar', Icon: null, text: pil.label, color: pil.color });
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap gap-x-1.5 gap-y-0.5 mt-0.5" style={{ color: fg }} data-testid={`cell-chips-${post.id}`}>
      {chips.map((c) => (
        <span
          key={c.key}
          className={`inline-flex items-center gap-0.5 text-[10px] leading-tight capitalize truncate max-w-full ${c.color ? 'px-1 rounded-sm text-white font-semibold' : 'opacity-85'}`}
          style={c.color ? { backgroundColor: c.color } : undefined}
          data-testid={c.key === 'pilar' ? `cell-pilar-${post.id}` : undefined}
        >
          {c.Icon && <c.Icon className="w-2 h-2 shrink-0" />}{c.text}
        </span>
      ))}
    </div>
  );
}

// Tipo de pieza con icono
const TYPE_ICONS = { reel: Clapperboard, post: ImageIcon, carrusel: GalleryHorizontal, story: CircleDashed, video: Film };
const TYPE_LABELS = { reel: 'Reel', post: 'Post', carrusel: 'Carrusel', story: 'Story', video: 'Vídeo' };
export function PostTypeIcon({ tipo, className = 'w-3.5 h-3.5' }) {
  const Icon = TYPE_ICONS[tipo];
  if (!Icon) return null;
  return <span title={TYPE_LABELS[tipo]} className="inline-flex" data-testid={`type-icon-${tipo}`}><Icon className={className} /></span>;
}

// Responsables (inicial o avatar). Solo para el equipo.
const personOf = (team, id) => (id ? team.find((m) => m.id === id) : null);
export function ResponsibleAvatars({ post, team = [] }) {
  const people = [personOf(team, post.responsable_id), personOf(team, post.responsable_portada_id)]
    .filter((p, i, arr) => p && arr.findIndex((x) => x?.id === p.id) === i);
  if (!people.length) return null;
  return (
    <span className="inline-flex -space-x-1" data-testid={`cell-responsable-${post.id}`}>
      {people.map((m) => (
        <span key={m.id} title={m.name}
          className="w-[18px] h-[18px] rounded-full bg-white text-[9px] font-bold flex items-center justify-center border border-black/10 overflow-hidden text-slate-700 shadow-sm">
          {m.avatar && String(m.avatar).startsWith('http') ? <img src={m.avatar} alt="" className="w-full h-full object-cover" /> : (m.name || '?').charAt(0).toUpperCase()}
        </span>
      ))}
    </span>
  );
}

// Tarjeta limpia de la publicación en la celda del calendario (estilo "ficha")
const Pill = ({ bg, fg, dot, children, testId }) => (
  <span className="inline-flex items-center gap-1.5 max-w-full px-2 py-0.5 rounded-full text-xs font-medium leading-tight" style={{ backgroundColor: bg, color: fg }} data-testid={testId}>
    {dot && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: dot }} />}
    <span className="truncate">{children}</span>
  </span>
);

export function PostCalendarCard({ post, team, showTeam, visible, locked }) {
  const meta = getStateMeta(post.estado);
  const pil = getPillarMeta(post.pilar);
  const thumbs = [
    ...(post.portada_file_id ? [{ file_id: post.portada_file_id, kind: 'image', portada: true }] : []),
    ...(post.material_preview || []).filter((m) => m.kind === 'image'),
  ].slice(0, 2);
  const firstFile = (post.material_preview || []).find((m) => m.kind !== 'image');
  const extra = Math.max(0, (post.material_count || 0) - (post.material_preview || []).length);
  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <div className="flex items-center gap-1.5 min-w-0">
        {post.color_portada_file_id
          ? <img src={contentCalendarAPI.getAttachmentUrl(post.id, post.color_portada_file_id)} alt="" loading="lazy" className="w-3.5 h-3.5 rounded-[3px] object-cover shrink-0 border" title="Color de portada" data-testid={`post-color-swatch-${post.id}`} />
          : <span className="w-3.5 h-3.5 rounded-[3px] bg-muted border shrink-0" title="Sin color de portada" />}
        {locked && <Lock className="w-3 h-3 shrink-0 opacity-60" />}
        <span className="text-sm font-semibold truncate text-foreground" data-testid={`post-title-${post.id}`}>{post.titulo || (post.copy || '').slice(0, 40) || 'Sin título'}</span>
      </div>
      <div className="flex flex-wrap gap-1">
        <Pill bg={meta.bg} fg={meta.fg} dot={meta.fg} testId={`post-state-badge-${post.id}`}>{meta.label}</Pill>
        {post.portada_file_id && <Pill bg="#E1F5EE" fg="#0F6E56" dot="#1D9E75" testId={`post-portada-ok-${post.id}`}>Portada OK</Pill>}
      </div>
      {post.aprobado_cliente && (
        <span className="inline-flex items-center gap-1.5 text-[11px] text-foreground" data-testid={`post-approved-${post.id}`}>
          <CheckSquare className="w-3.5 h-3.5 text-blue-600 shrink-0" /> Aprobado por el cliente
        </span>
      )}
      {(thumbs.length > 0 || firstFile) && (
        <div className="flex items-center gap-1 min-w-0" data-testid={`post-material-${post.id}`}>
          {thumbs.map((t) => (
            <img key={t.file_id} src={contentCalendarAPI.getAttachmentUrl(post.id, t.file_id)} alt="" loading="lazy"
              className={`h-9 w-9 object-cover rounded shrink-0 ${t.portada ? 'ring-1 ring-emerald-400' : 'border'}`} title={t.portada ? 'Portada' : t.name}
              data-testid={t.portada ? `post-cover-thumb-${post.id}` : undefined} />
          ))}
          {firstFile && (
            <span className="inline-flex items-center gap-1 min-w-0 px-1.5 py-0.5 rounded bg-muted text-[11px] text-muted-foreground" title={firstFile.name}>
              <FileIcon className="w-3 h-3 shrink-0" /><span className="truncate">{firstFile.name}</span>
            </span>
          )}
          {extra > 0 && <span className="text-[10px] text-muted-foreground shrink-0">+{extra}</span>}
        </div>
      )}
      {(visible.pilar && pil) || (visible.quien_graba && post.quien_graba) || (visible.automatizacion && post.automatizacion) ? (
        <div className="flex flex-wrap gap-1">
          {visible.pilar && pil && <Pill bg={`${pil.color}22`} fg="#334155" testId={`cell-pilar-${post.id}`}>{pil.label}</Pill>}
          {visible.quien_graba && post.quien_graba && <Pill bg="#F1F5F9" fg="#334155" testId={`cell-quien-graba-${post.id}`}><Video className="w-3 h-3 inline -mt-px mr-0.5" />Graba: {post.quien_graba}</Pill>}
          {visible.automatizacion && post.automatizacion && <Pill bg="#FEF3C7" fg="#92400E" testId={`cell-auto-${post.id}`}><Zap className="w-3 h-3 inline -mt-px mr-0.5" />Auto</Pill>}
        </div>
      ) : null}
      <div className="flex items-center gap-1.5 text-muted-foreground pt-1 border-t border-dashed">
        <PostTypeIcon tipo={post.tipo} />
        <ChannelIconsRow post={post} className="w-3.5 h-3.5" colored testId={`cell-channels-${post.id}`} />
        {showTeam && <span className="ml-auto"><ResponsibleAvatars post={post} team={team} /></span>}
      </div>
    </div>
  );
}

// Ficha completa (tooltip al pasar el ratón)
export function PostHoverCard({ post, team = null }) {
  const meta = getStateMeta(post.estado);
  const channels = getPostChannels(post);
  const Row = ({ label, children }) => (
    <div className="flex gap-2 text-[11px]"><span className="w-20 shrink-0 text-muted-foreground">{label}</span><span className="min-w-0 flex-1 truncate">{children || '—'}</span></div>
  );
  return (
    <div className="w-64 space-y-1" data-testid={`post-hover-${post.id}`}>
      <div className="flex items-start gap-2">
        {post.portada_file_id && <img src={contentCalendarAPI.getAttachmentUrl(post.id, post.portada_file_id)} alt="" className="w-12 h-12 object-cover rounded border" />}
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold leading-tight line-clamp-2">{post.titulo || 'Sin título'}</p>
          <span className="inline-block mt-0.5 text-[9px] font-semibold px-1 py-px rounded" style={{ backgroundColor: meta.bg, color: meta.fg }}>{meta.label}</span>
        </div>
        {post.color_portada_file_id && <img src={contentCalendarAPI.getAttachmentUrl(post.id, post.color_portada_file_id)} alt="Color portada" className="w-6 h-6 rounded border object-cover" title="Color de portada" />}
      </div>
      <Row label="Redes">{channels.length ? channels.map((c) => getChannelMeta(c).label).join(', ') : null}</Row>
      <Row label="Tipo">{TYPE_LABELS[post.tipo] || post.tipo}</Row>
      {team && <Row label="Responsable">{personOf(team, post.responsable_id)?.name}</Row>}
      {team && post.responsable_portada_id && <Row label="Portada">{personOf(team, post.responsable_portada_id)?.name}</Row>}
      <Row label="Pilar">{getPillarMeta(post.pilar) ? <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: getPillarMeta(post.pilar).color }} />{getPillarMeta(post.pilar).label}</span> : null}</Row>
      <Row label="Automatización">{post.automatizacion ? 'Sí' : 'No'}</Row>
      <Row label="Quién graba">{post.quien_graba}</Row>
      <Row label="Color portada"><span className="inline-flex items-center gap-1"><Palette className="w-3 h-3" />{post.color_portada_file_id ? 'Subido' : 'Sin subir'}</span></Row>
    </div>
  );
}
